import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ErgTip {
  area: string;
  areaEn: string;
  icon: string;
  tips: { title: string; detail: string }[];
  color: string;
}

const ERGO_TIPS: ErgTip[] = [
  {
    area: '坐姿与脊柱',
    areaEn: 'Posture & Spine',
    icon: '🪑',
    tips: [
      { title: '90°膝关节', detail: '大腿与地面平行，双脚平放地面' },
      { title: '腰椎支撑', detail: '使用靠垫维持腰椎自然前凸曲线' },
      { title: '肩部放松', detail: '双肩自然下沉，避免耸肩紧张' },
    ],
    color: '#4ECDC4',
  },
  {
    area: '屏幕与视觉',
    areaEn: 'Screen & Vision',
    icon: '🖥️',
    tips: [
      { title: '屏幕高度', detail: '屏幕顶部与视线平齐或略低' },
      { title: '20-20-20法则', detail: '每20分钟看20英尺外20秒' },
      { title: '距离50-70cm', detail: '屏幕距眼睛一臂距离约50-70厘米' },
    ],
    color: '#667EEA',
  },
  {
    area: '键鼠与手腕',
    areaEn: 'Keyboard & Wrist',
    icon: '⌨️',
    tips: [
      { title: '手腕中立位', detail: '打字时手腕保持自然伸直不弯曲' },
      { title: '肘部90°', detail: '前臂与桌面平行，肘部约成直角' },
      { title: '鼠标就近', detail: '鼠标紧靠键盘，减少手臂外展' },
    ],
    color: '#FF6B35',
  },
  {
    area: '活动与休息',
    areaEn: 'Movement & Rest',
    icon: '🏃',
    tips: [
      { title: '定时起身', detail: '每45-60分钟站立活动3-5分钟' },
      { title: '颈椎操', detail: '左右转头、前后点头各5次舒缓' },
      { title: '眼部放松', detail: '闭眼深呼吸，热敷眼部缓解疲劳' },
    ],
    color: '#2ECC71',
  },
];

export const ErgonomicsAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentAreaIdx = Math.min(
    Math.floor(Math.max(0, frame - 50) / 115),
    ERGO_TIPS.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0B1120 0%, #111827 50%, #0B1120 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4ECDC4, #2ECC71)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          人体工程学指南
        </h1>
        <p style={{ fontSize: 28, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Ergonomics · 科学办公 远离职业病
        </p>
      </div>

      {/* Area tabs */}
      <div
        style={{
          position: 'absolute',
          top: 280,
          left: 55,
          right: 55,
          display: 'flex',
          gap: 14,
        }}
      >
        {ERGO_TIPS.map((tip, i) => {
          const tabProg = spring({
            frame: frame - 15 - i * 8,
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const isActive = i === currentAreaIdx;

          return (
            <div
              key={tip.areaEn}
              style={{
                flex: 1,
                padding: '14px 18px',
                background: isActive ? `${tip.color}18` : 'rgba(255,255,255,0.03)',
                border: `2px solid ${isActive ? tip.color + '50' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 14,
                textAlign: 'center',
                opacity: tabProg,
                boxShadow: isActive ? `0 4px 20px ${tip.color}20` : 'none',
              }}
            >
              <span style={{ fontSize: 50 }}>{tip.icon}</span>
              <div
                style={{
                  fontSize: 30,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? tip.color : 'rgba(255,255,255,0.4)',
                  marginTop: 4,
                }}
              >
                {tip.area}
              </div>
            </div>
          );
        })}
      </div>

      {/* Area detail cards */}
      {ERGO_TIPS.map((area, i) => {
        const phaseStart = 50 + i * 115;
        const phaseEnd = phaseStart + 110;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 18], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 12, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === ERGO_TIPS.length - 1 ? fadeIn : fadeIn * fadeOut;

        if (frame < phaseStart || opacity <= 0) return null;

        return (
          <div
            key={area.areaEn}
            style={{
              position: 'absolute',
              top: 500,
              left: 55,
              right: 55,
              opacity,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
              <span style={{ fontSize: 48 }}>{area.icon}</span>
              <div>
                <h2 style={{ fontSize: 42, fontWeight: 700, color: area.color, margin: 0 }}>
                  {area.area}
                </h2>
                <span style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)' }}>{area.areaEn}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 20 }}>
              {area.tips.map((tip, ti) => {
                const tipProg = spring({
                  frame: frame - phaseStart - 15 - ti * 10,
                  fps,
                  config: { damping: 14, stiffness: 120 },
                });

                return (
                  <div
                    key={tip.title}
                    style={{
                      flex: 1,
                      padding: '28px 30px',
                      background: `${area.color}08`,
                      border: `2px solid ${area.color}25`,
                      borderRadius: 20,
                      opacity: tipProg,
                      transform: `translateY(${interpolate(tipProg, [0, 1], [20, 0])}px)`,
                    }}
                  >
                    <h4
                      style={{
                        fontSize: 34,
                        fontWeight: 700,
                        color: area.color,
                        margin: '0 0 12px',
                      }}
                    >
                      {tip.title}
                    </h4>
                    <p style={{ fontSize: 26, color: '#D1D5DB', lineHeight: 1.6, margin: 0 }}>
                      {tip.detail}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Visual measurement indicator */}
            <div
              style={{
                marginTop: 28,
                padding: '18px 24px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 16,
                display: 'flex',
                justifyContent: 'center',
                gap: 40,
              }}
            >
              {['⚠️ 错误姿势导致疼痛', '✅ 正确姿势预防损伤', '💡 每天坚持 = 终身受益'].map(
                (text, ti) => (
                  <span key={ti} style={{ fontSize: 26, color: 'rgba(255,255,255,0.5)' }}>
                    {text}
                  </span>
                ),
              )}
            </div>
          </div>
        );
      })}

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        {ERGO_TIPS.map((a, i) => (
          <div
            key={a.areaEn}
            style={{
              width: i === currentAreaIdx ? 44 : 12,
              height: 12,
              borderRadius: 6,
              background: i === currentAreaIdx ? a.color : 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
